<template>
  <div class="p-6">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Prototipos V2
      </h1>
      <p class="text-gray-600 dark:text-gray-400">
        Sistema de prototipos con catálogos dinámicos
      </p>
    </div>

    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <div class="flex justify-between items-center">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
            Lista de Prototipos
          </h2>
          <router-link
            to="/prototipos-new/nuevo"
            class="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            Nuevo Prototipo
          </router-link>
        </div>
      </div>

      <div class="p-6">
        <div v-if="loading" class="text-center py-8">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto"></div>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 dark:text-gray-300 border-b-2 border-gray-200 dark:border-gray-700">
              <tr>
                <th class="px-4 py-2 font-bold">Folio</th>
                <th class="px-4 py-2 font-bold">Cliente</th>
                <th class="px-4 py-2 font-bold">Proyecto</th>
                <th class="px-4 py-2 font-bold">Estado</th>
                <th class="px-4 py-2 font-bold text-right">Costo Unit.</th>
                <th class="px-4 py-2 font-bold">Fecha</th>
                <th class="px-4 py-2 font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="prototipo in prototipos"
                :key="prototipo.prototipoId"
                class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <td class="px-4 py-2 font-medium text-gray-900 dark:text-white">
                  {{ prototipo.folio }}
                </td>
                <td class="px-4 py-2 text-gray-700 dark:text-gray-200">
                  {{ prototipo.cliente }}
                </td>
                <td class="px-4 py-2 text-gray-700 dark:text-gray-200">
                  {{ prototipo.proyecto }}
                </td>
                <td class="px-4 py-2">
                  <span :class="getEstadoClass(prototipo.estado)" class="px-2 py-1 text-xs font-medium rounded-full">
                    {{ prototipo.estado }}
                  </span>
                </td>
                <td class="px-4 py-2 text-right">
                  <span class="font-semibold text-green-600 dark:text-green-400">
                    ${{ formatCurrency(prototipo.costoUnitario || 0) }}
                  </span>
                </td>
                <td class="px-4 py-2 text-gray-700 dark:text-gray-200">
                  {{ formatDate(prototipo.fecha) }}
                </td>
                <td class="px-4 py-2">
                  <div class="flex gap-2">
                    <button
                      @click="imprimirPDF(prototipo)"
                      class="text-violet-600 hover:text-violet-800 dark:text-violet-400 dark:hover:text-violet-300"
                      title="Imprimir PDF"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/>
                      </svg>
                    </button>
                    <router-link
                      :to="`/prototipos-new/${prototipo.prototipoId}/editar`"
                      class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      title="Editar"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </router-link>
                    <button
                      @click="eliminar(prototipo)"
                      class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      title="Eliminar"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="prototipos.length === 0">
                <td colspan="7" class="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  No hay prototipos registrados
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted } from 'vue'
import { useCatalogosNew } from '../../composables/useCatalogosNew'
import { usePrototipoPDF } from '../../composables/usePrototipoPDF'
import Swal from 'sweetalert2'

const {
  loading,
  prototipos,
  fetchPrototipos,
  deletePrototipo,
  fetchPrototipo
} = useCatalogosNew()

const { generarPDFEspecificacion } = usePrototipoPDF()

onMounted(async () => {
  await fetchPrototipos()
})

const getEstadoClass = (estado) => {
  const classes = {
    'Borrador': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    'Cotizado': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    'Aprobado': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    'Rechazado': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    'Cancelado': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
  }
  return classes[estado] || classes['Borrador']
}

const formatDate = (date) => {
  if (!date) return '-'
  return new Date(date).toLocaleDateString('es-MX')
}

const eliminar = async (prototipo) => {
  const result = await Swal.fire({
    title: '¿Eliminar prototipo?',
    html: `¿Está seguro de eliminar el prototipo <strong>${prototipo.folio}</strong>?<br><small class="text-gray-500">Esta acción no se puede deshacer</small>`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc2626',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  })

  if (result.isConfirmed) {
    try {
      await deletePrototipo(prototipo.prototipoId)

      await Swal.fire({
        icon: 'success',
        title: '¡Eliminado!',
        text: 'El prototipo ha sido eliminado correctamente',
        confirmButtonColor: '#7c3aed'
      })
    } catch (error) {
      console.error('Error al eliminar:', error)

      await Swal.fire({
        icon: 'error',
        title: 'Error al eliminar',
        text: error.response?.data?.error || error.message || 'Ocurrió un error al eliminar el prototipo',
        confirmButtonColor: '#7c3aed'
      })
    }
  }
}

const formatCurrency = (value) => {
  if (!value && value !== 0) return '0.00'
  return Number(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const imprimirPDF = async (prototipo) => {
  try {
    // Mostrar loading
    Swal.fire({
      title: 'Generando PDF...',
      html: 'Por favor espere mientras se genera el documento',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading()
      }
    })

    // Obtener datos completos del prototipo
    const prototipoCompleto = await fetchPrototipo(prototipo.prototipoId)

    // Cerrar loading
    Swal.close()

    // Generar PDF
    generarPDFEspecificacion(prototipoCompleto)

    // Mostrar mensaje de éxito
    await Swal.fire({
      icon: 'success',
      title: '¡PDF Generado!',
      text: 'El documento se ha descargado correctamente',
      timer: 2000,
      showConfirmButton: false
    })
  } catch (error) {
    console.error('Error al generar PDF:', error)

    await Swal.fire({
      icon: 'error',
      title: 'Error al generar PDF',
      text: error.response?.data?.error || error.message || 'Ocurrió un error al generar el documento',
      confirmButtonColor: '#7c3aed'
    })
  }
}
</script>
